import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateProfile, getAccountInfo } from '../../redux/actions/auth';
import { showToastNotif } from '../../redux/actions/notification';
import analytic, {
  analyticEvents,
  analyticTypes
} from '../../service/analytic';
import Button from '../Button/Button';

import styles from './profileForm.module.css';
import InputForm from '../InputForm/InputForm';

const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

function ProfileForm() {
  const pic = useRef();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const { user } = auth;
  const [hasPicture, setHasPicture] = useState(false);
  const profile = auth?.user?.profile;
  const [name, setName] = useState(profile?.name);
  const [submitDisabled, setSubmitDisabled] = useState(true);

  useEffect(() => {
    analytic(analyticTypes.trackPage, 'EditProfile', { user });
  }, [user]);

  const initial = useMemo(() => {
    return (
      profile &&
      profile.name
        .split(' ')
        .slice(0, 2)
        .map((n) => n.charAt(0).toUpperCase())
        .reduce((n, space) => n + space)
    );
  }, [profile]);

  const onClickProfile = () => {
    pic.current.click();
  };

  const doUpdatePic = useCallback(async () => {
    const payload = new FormData();
    payload.set('name', name);
    pic.current.files[0] && payload.append('avatar', pic.current.files[0]);
    const res = await dispatch(updateProfile(profile.id, payload));
    if (res) {
      dispatch(getAccountInfo({ refreshAccountInfo: true }));
      analytic(analyticTypes.event, analyticEvents.PROFILE.EDIT_PROFILE, {
        user
      });
      return dispatch(
        showToastNotif('success', 'Your profile picture has been updated')
      );
    }
  }, [dispatch, name, profile.id, user]);

  const onPicSelected = useCallback(() => {
    if (pic.current.files.length) {
      const image = pic.current.files[0];
      if (!allowedTypes.includes(image.type)) return;
      const picture = URL.createObjectURL(image);
      setHasPicture(picture);
      doUpdatePic();
    }
  }, [pic, doUpdatePic]);

  const onSubmit = useCallback(() => {
    const doUpdateProfile = async () => {
      const payload = new FormData();
      payload.set('name', name);
      pic.current.files[0] && payload.append('avatar', pic.current.files[0]);
      const res = await dispatch(updateProfile(profile.id, payload));
      if (res) {
        dispatch(getAccountInfo({ refreshAccountInfo: true }));
        analytic(analyticTypes.event, analyticEvents.PROFILE.EDIT_PROFILE, {
          user
        });
        setSubmitDisabled(true);
        return dispatch(
          showToastNotif('success', 'Your profile has been updated')
        );
      }
    };
    doUpdateProfile();
  }, [pic, name, profile, dispatch, user]);

  const showAvatar = useMemo(() => {
    return !hasPicture && profile.avatar;
  }, [hasPicture, profile]);

  const showInitial = useMemo(() => {
    return !hasPicture && !profile.avatar;
  }, [hasPicture, profile]);

  const handleInputForm = (event) => {
    const eventValue = event.target.value;

    if (eventValue === profile.name) {
      return setSubmitDisabled(true);
    }
    setName(eventValue);
    setSubmitDisabled(false);
  };

  return (
    <div className={styles['formWrapper']}>
      <div className={styles['formInput']}>
        <div className={styles['inputLabel']}>Profile Picture</div>
        <div className={styles['profileForm']}>
          <div>
            {hasPicture && (
              <img
                className={styles['avatar']}
                alt='profile_pic'
                src={hasPicture}
                width='100'
                height='100'
              />
            )}
            {showAvatar && (
              <img
                className={styles['avatar']}
                alt='profile_pic'
                src={profile.avatar}
                width='100'
                height='100'
              />
            )}
            {showInitial && <span className={styles['avatar']}>{initial}</span>}
          </div>
          <section className={styles['uploader']}>
            <input
              onChange={onPicSelected}
              type='file'
              accept='image/x-png,image/gif,image/jpeg,image/png'
              capture='camera'
              hidden
              ref={pic}
            />
            <Button
              onClick={onClickProfile}
              shape='rounded'
              bgColor='dark'
              size='large'
            >
              Change Profile Picture
            </Button>
            <span>Must be JPEG, PNG, or GIF and cannot exceed 10MB</span>
          </section>
        </div>
      </div>
      <div className={styles['nameForm']}>
        <div className={styles['inputLabel']}>Name</div>
        <div className={styles['inputButton']}>
          <form className={styles['nameInput']}>
            <InputForm
              id='name'
              errorClassName={styles['inputError']}
              placeholder='e.g. 81234567890'
              defaultValue={profile.name}
              className={styles['inputForm']}
              onChange={(e) => {
                handleInputForm(e);
              }}
            />
          </form>
          {!submitDisabled && (
            <div className={styles['buttonWrapper']}>
              <Button bgColor='sushiroll' onClick={onSubmit}>
                Save
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileForm;
